import { ChangeEvent, ReactNode, useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isEmpty } from 'ramda'

import PlainButton from 'app/common/ui-next/plain-button'
import PlainConfirmModal from 'app/common/ui-next/plain-confirm-modal'
import usePopup from 'app/common/hooks/usePopup'

import { DataItem, extractData } from './extractData'
import { useCustomerLimitSubmit } from '../../dataProviders/customer/hooks'

import styles from './MultipleLimitsEditForm.module.scss'

const formID = 'multiple_limits' as const

type Props = {
  customerID: string
  children: (props: {
    onModify: (val: boolean) => void
    sending: boolean
    resetKey: Array<never>
    onClearLimit: (sellPointID: string) => void
  }) => ReactNode
  loading?: boolean
  onSuccess?: () => void
}

export const MultipleLimitsEditForm = ({
  customerID,
  children,
  loading = false,
  onSuccess,
}: Props) => {
  const { t } = useTranslation('cafeteria')
  const popup = usePopup()

  const formRef = useRef<HTMLFormElement | null>(null)

  const [sending, setSending] = useState(false)
  const [modify, setModify] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [resetKey, setResetKey] = useState([])

  const { submit } = useCustomerLimitSubmit({ params: { customerID }, onSuccess })

  const handleSubmitConfirmed = useCallback(async () => {
    if (!formRef.current) return
    setShowConfirmModal(false)
    setSending(true)
    const res = await submit({
      sellPointsPersonalLimits: extractData(new FormData(formRef.current)),
    })
    if (res) {
      popup(t('settings_limit_multiple_submit_success', 'Лимиты успешно установлены'))
    }
    setModify(false)
    setResetKey([])
    setSending(false)
  }, [submit, popup, t])

  const handleValidate = useCallback((extractedData: Array<DataItem>) => {
    // добавить сюда код, если нужно проверять на значение больше максимума
    return !isEmpty(extractedData)
  }, [])

  const handleSubmit = useCallback(
    (e: ChangeEvent<HTMLFormElement>) => {
      e.preventDefault()
      const { id } = e.target
      if (id !== formID) return
      const formData = new FormData(e.target)
      const extractedData = extractData(formData)

      if (handleValidate(extractedData)) {
        setShowConfirmModal(true)
      }
    },
    [handleValidate],
  )

  const handleModify = useCallback((val: boolean) => {
    if (val && formRef.current) {
      const data = extractData(new FormData(formRef.current))
      setModify(!isEmpty(data))
    }
  }, [])

  const handleClearLimit = useCallback(
    async (sellPointID: string) => {
      console.log('handleClearLimit')
      setSending(true)
      const res = await submit({ sellPointsPersonalLimits: [{ sellPointID, value: null }] })
      if (res) {
        popup(t('settings_limit_clear_success', 'Лимит успешно cнят'))
      }
      setSending(false)
    },
    [submit, popup, t],
  )

  return (
    <>
      <form onSubmit={handleSubmit} id={formID} ref={formRef}>
        {children({
          onModify: handleModify,
          sending,
          resetKey,
          onClearLimit: handleClearLimit,
        })}
        <PlainButton
          type="submit"
          className={styles.submitButton}
          disabled={loading || sending || !modify}
        >
          {sending
            ? t('settings_limit_multiple_set_bt_process', 'Установка\u00A0лимитов...')
            : t('settings_limit_multiple_set_bt', 'Установить\u00A0лимиты')}
        </PlainButton>
      </form>

      {showConfirmModal && (
        <PlainConfirmModal
          messageText={t(
            'settings_limit_multiple_set_prompt_new',
            'Установить новые лимиты для точек продаж? Вы уверены?',
          )}
          onCancel={() => setShowConfirmModal(false)}
          onConfirm={handleSubmitConfirmed}
          confirmButtonTitle={t('settings_limit_personal_set_confirm', 'Да') || ''}
          cancelButtonTitle={t('settings_limit_personal_set_cancel', 'Отмена') || ''}
        />
      )}
    </>
  )
}
